import type { ComponentProps } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from 'cn';
import { Button as ManagedButton, buttonVariants as managedVariants } from '../ui/button';

type ButtonSize = ComponentProps<typeof ManagedButton>['size'] | 'touch' | 'bare';
export type ButtonProps = Omit<ComponentProps<typeof ManagedButton>, 'size' | 'className'> & {
  className?: string;
  size?: ButtonSize;
  shape?: 'pill' | 'menu' | 'default';
};

// LoopTasker's app-owned shape scale. Native form submit behavior is retained
// for existing Tragenda call sites that intentionally omit type="button".
export function buttonVariants({ variant = 'default', size = 'touch', shape = 'pill', className }: {
  variant?: ButtonProps['variant']; size?: ButtonSize; shape?: ButtonProps['shape']; className?: string;
} = {}) {
  return cn(managedVariants({ variant, size: size === 'touch' ? 'lg' : size === 'bare' ? 'default' : size }),
    shape === 'pill' && 'rounded-full', shape === 'menu' && 'rounded-sm',
    size === 'touch' && 'min-h-11 h-auto px-5 py-2.5',
    size === 'bare' && 'h-auto min-w-0 justify-normal gap-0 p-0 font-normal whitespace-normal', className);
}

export function Button({ className, size = 'touch', shape = 'pill', type = 'submit', variant = 'default', ...props }: ButtonProps) {
  return <ManagedButton {...props} type={type} variant={variant} size={size === 'touch' ? 'lg' : size === 'bare' ? 'default' : size}
    data-app-size={size} data-shape={shape} className={buttonVariants({ variant, size, shape, className })} />;
}

// A link stays a link: Base UI's Button applies role="button" even with render.
export function ButtonLink({ className, ...props }: LinkProps) {
  return <Link {...props} data-slot="button-link" className={cn(buttonVariants(), className)} />;
}
